import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import urlJoin from 'url-join';
import { cookieUtils } from '../utils/cookies';
import { getDeviceId } from '../utils/deviceId';
import { AuthResponse } from '../types';

// Get API base URL from environment variables
// Note: In Vite, environment variables must be prefixed with VITE_ to be accessible
// The value is read from .env file: VITE_API_BASE_URL=http://localhost:3000/api
// Supports both VITE_API_BASE_URL and VITE_BACKEND_URL for backward compatibility
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'http://localhost:3000/api';

// Create axios instance with default config
const http: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '10000', 10),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include http-only cookies (refresh token) in requests
});

// Request interceptor
http.interceptors.request.use(
  (config) => {
    // Handle URL joining to prevent double slashes and base URL duplication
    if (config.url) {
      // If URL is already absolute (starts with http:// or https://), use it as-is
      if (config.url.startsWith('http://') || config.url.startsWith('https://')) {
        // URL is already absolute, don't modify it
        return config;
      }

      // If baseURL is already set and URL is relative, let axios handle it
      // Just ensure the URL starts with a slash for proper joining
      if (!config.url.startsWith('/')) {
        config.url = '/' + config.url;
      }
    }

    // Add auth token if available (from cookies)
    const token = cookieUtils.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Track the refresh promise to handle concurrent requests in the same tab
let refreshPromise: Promise<string> | null = null;

// Response interceptor
http.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle common errors
    if (error.response) {
      // Server responded with error status
      switch (error.response.status) {
        case 401:
          // Unauthorized - try to refresh token
          const requestUrl = error.config?.url || '';
          const isAuthEndpoint = requestUrl.includes('/auth/login') ||
            requestUrl.includes('/auth/register') ||
            requestUrl.includes('/auth/google') ||
            requestUrl.includes('/auth/twitter') ||
            requestUrl.includes('/auth/refresh') ||
            requestUrl.includes('/auth/change-password');

          // Don't try to refresh if this is an auth endpoint or refresh endpoint
          // Also don't retry if the request has already been retried
          if (isAuthEndpoint || originalRequest._retry) {
            // For auth endpoints, just clear tokens and redirect if needed
            if (isAuthEndpoint && !requestUrl.includes('/auth/change-password')) {
              cookieUtils.clearTokens();

              if (!requestUrl.includes('/auth/refresh') &&
                !window.location.pathname.includes('/login') &&
                !window.location.pathname.includes('/signup')) {
                window.location.href = '/login';
              }
            }
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          // Check if another tab already refreshed the token
          const currentToken = cookieUtils.getAccessToken();
          const failedToken = originalRequest.headers?.Authorization?.toString().split(' ')[1];

          // If we have a token in cookies that is different from the one that failed,
          // it implies another tab (or earlier request) has already refreshed it.
          // Note: ensure currentToken is valid/exists before assuming it's fresh.
          if (currentToken && failedToken && currentToken !== failedToken) {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${currentToken}`;
            }
            return http(originalRequest);
          }

          // If a refresh is already in progress, wait for it
          if (refreshPromise) {
            try {
              const token = await refreshPromise;
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return http(originalRequest);
            } catch (err) {
              return Promise.reject(err);
            }
          }

          // Start a new refresh process
          refreshPromise = (async () => {
            try {
              // Create a new axios instance without interceptors
              const refreshAxios = axios.create({
                baseURL: API_BASE_URL,
                timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '10000', 10),
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
              });

              const deviceId = getDeviceId();
              const refreshBody = deviceId ? { deviceId } : {};

              const refreshResponse = await refreshAxios.post<AuthResponse>(
                '/auth/refresh',
                refreshBody,
                {
                  withCredentials: true,
                }
              );

              let refreshData: AuthResponse;
              if (typeof refreshResponse.data === 'object' && refreshResponse.data !== null && 'success' in refreshResponse.data) {
                const apiResponse = refreshResponse.data as any;
                refreshData = apiResponse.success ? apiResponse.data : refreshResponse.data as AuthResponse;
              } else {
                refreshData = refreshResponse.data as AuthResponse;
              }

              if (refreshData.accessToken) {
                cookieUtils.setAccessToken(refreshData.accessToken);
                return refreshData.accessToken;
              } else {
                throw new Error('No access token in refresh response');
              }
            } catch (refreshError) {
              cookieUtils.clearTokens();
              if (!window.location.pathname.includes('/login') &&
                !window.location.pathname.includes('/signup')) {
                window.location.href = '/login';
              }
              throw refreshError;
            } finally {
              refreshPromise = null;
            }
          })();

          // Wait for the newly created refresh promise
          try {
            const token = await refreshPromise;
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return http(originalRequest);
          } catch (err) {
            return Promise.reject(err);
          }

        case 403:
          console.error('Access forbidden');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error');
          break;
        default:
          console.error('API Error:', error.response.data);
      }
    } else if (error.request) {
      console.error('Network error - no response received');
    } else {
      console.error('Error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default http;

